<script setup lang="ts">
import { CheckIcon, ExternalLinkIcon, LogOutIcon, Undo2Icon, XIcon } from '@lucide/vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue-sonner';

import { logout, profileQueryOptions } from '@/entities/profile';
import { leaveTeamMutation, teamKeys, teamQueryOptions } from '@/entities/team';
import { IS_BILLING_ENABLED, IS_HELPDESK_ENABLED } from '@/shared/config';
import { useGlobalLoader } from '@/shared/hooks';
import { useTeamCookie } from '@/shared/hooks/use-team-cookie';
import { useTeamSwitch } from '@/shared/hooks/use-team-switch';
import LogoGradient from '@/shared/icons/LogoGradient.vue';
import { cn } from '@/shared/lib/misc';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Button, buttonVariants } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const { data: profile } = useQuery(profileQueryOptions());
const { currentTeamUuid, clearTeamCookie } = useTeamCookie();
const { data: team } = useQuery(teamQueryOptions(currentTeamUuid));
const { switchToPersonalAccount } = useTeamSwitch();
const { toggle: toggleGlobalLoader } = useGlobalLoader();

const isCancelable = computed(() => route.name !== 'team/new' && !!team.value);

const steps = computed(() => {
  const list: { key: string; label: string }[] = [{ key: 'team/new', label: 'Create' }];
  if (IS_BILLING_ENABLED) list.push({ key: 'team/billing', label: 'Billing' });
  list.push({ key: 'team/invite', label: 'Invite' });
  return list;
});

const currentStepIndex = computed(() => steps.value.findIndex((step) => step.key === route.name));

const { mutateAsync: deleteTeamMutate } = useMutation(leaveTeamMutation(currentTeamUuid));
const isCanceling = ref(false);
const isReturning = ref(false);

async function handleLogout() {
  await logout();
  window.localStorage.clear();
  queryClient.clear();
  clearTeamCookie();
  router.push({ name: 'login', query: { logout: 'success' } }).catch(() => {});
}

async function handleCancel() {
  if (!team.value) return;

  isCanceling.value = true;
  toggleGlobalLoader('Canceling team setup...');

  try {
    await deleteTeamMutate();
    await queryClient.invalidateQueries({ queryKey: teamKeys.teams() });
    await switchToPersonalAccount();
    await router.push({ name: 'overview' });
  } catch (err) {
    window.console.error(err);
    toast.error('Failed to cancel team setup');
  } finally {
    isCanceling.value = false;
    toggleGlobalLoader();
  }
}

async function handleReturnToPersonal() {
  isReturning.value = true;
  toggleGlobalLoader('Switching to personal account...');

  try {
    await switchToPersonalAccount();
    await router.push({ name: 'overview' });
  } catch (err) {
    window.console.error(err);
    toast.error('Failed to switch to personal account');
  } finally {
    isReturning.value = false;
    toggleGlobalLoader();
  }
}
</script>

<template>
  <div class="bg-top-navigation flex min-h-screen flex-col">
    <header class="flex h-16 shrink-0 items-center justify-between border-b px-4 lg:px-8">
      <RouterLink to="/" class="flex items-center">
        <LogoGradient class="h-7 w-7" />
        <span class="ml-2 text-base font-medium">SimplyStack</span>
      </RouterLink>

      <div class="flex items-center gap-4">
        <RouterLink
          v-if="IS_HELPDESK_ENABLED"
          :to="{ name: 'support' }"
          target="_blank"
          :class="
            cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'hidden gap-1.5 md:inline-flex')
          "
        >
          Support
          <ExternalLinkIcon class="h-3.5 w-3.5" />
        </RouterLink>

        <DropdownMenu v-if="profile">
          <DropdownMenuTrigger>
            <Avatar class="size-9">
              <AvatarImage :src="profile.gravatar_url" alt="Profile picture" />
              <AvatarFallback>
                {{ profile.first_name[0] }}{{ profile.last_name[0] }}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-56" align="end">
            <div class="p-2">
              <h4 class="truncate text-sm font-medium">
                {{ profile.first_name }} {{ profile.last_name }}
              </h4>
              <p class="text-muted-foreground truncate text-xs">{{ profile.email }}</p>
            </div>
            <DropdownMenuItem @click="handleLogout">
              <LogOutIcon class="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <template v-if="isCancelable">
          <div class="bg-border h-6 w-px shrink-0" role="separator" aria-orientation="vertical" />

          <Tooltip>
            <TooltipTrigger as-child>
              <Button
                variant="secondary"
                size="icon"
                :disabled="isCanceling"
                aria-label="Cancel team setup"
                @click="handleCancel"
              >
                <XIcon class="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Cancel team setup</TooltipContent>
          </Tooltip>
        </template>
      </div>
    </header>

    <main class="flex flex-1 flex-col px-4 py-8 lg:px-8">
      <div class="mx-auto flex w-full max-w-xl flex-1 flex-col">
        <div class="mx-auto mb-10 flex w-full max-w-sm items-center">
          <template v-for="(step, index) in steps" :key="step.key">
            <div class="flex flex-col items-center gap-1.5">
              <div
                :class="
                  cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-medium transition-colors',
                    currentStepIndex > index && 'border-primary bg-primary text-primary-foreground',
                    currentStepIndex === index && 'border-primary text-primary',
                    currentStepIndex < index && 'border-muted text-muted-foreground',
                  )
                "
              >
                <CheckIcon v-if="currentStepIndex > index" class="h-4 w-4" />
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                :class="
                  cn(
                    'text-xs font-medium',
                    currentStepIndex >= index ? 'text-foreground' : 'text-muted-foreground',
                  )
                "
              >
                {{ step.label }}
              </span>
            </div>
            <div
              v-if="index < steps.length - 1"
              :class="
                cn(
                  'mx-2 -mt-5 h-px flex-1 transition-colors',
                  currentStepIndex > index ? 'bg-primary' : 'bg-muted',
                )
              "
            />
          </template>
        </div>

        <RouterView />

        <div v-if="isCancelable" class="mt-8 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            class="gap-2"
            :disabled="isReturning || isCanceling"
            @click="handleReturnToPersonal"
          >
            <Undo2Icon class="h-4 w-4" />
            Return to personal account
          </Button>
        </div>
      </div>
    </main>
  </div>
</template>
